// src/admin/screens/loans/LoanDetailScreen.tsx
// Loan decision surface. The hero header (gradient icon + status/risk chips +
// big amount title + back link + action row) follows the Thandizo Admin
// Console design; the action row, tab system, and modal mounting below are
// the existing production logic — we keep both the role-gated capability
// checks and the server-enforced status transition checks.
import { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ChevronLeft, Wallet, Check, X, Flag, MoreHorizontal,
  CreditCard, RefreshCcw, AlertOctagon, RotateCcw,
  type LucideIcon,
} from 'lucide-react';
import { useLoan } from '@admin/hooks/useLoan';
import { useRole } from '@admin/auth/useRole';
import Button from '@admin/components/Button';
import StatusChip from '@admin/components/StatusChip';
import Chip from '@admin/components/Chip';
import {
  LOAN_STATUS_LABEL,
  LOAN_STATUS_TONE,
  allowedActions,
  type LoanAction,
} from '@admin/utils/loanStatus';
import { formatKwacha } from '@admin/utils/money';
import { formatCatDateTime } from '@admin/utils/dates';
import type { Ngwee } from '@/lib/types';
import OverviewTab from './tabs/OverviewTab';
import ScheduleTab from './tabs/ScheduleTab';
import PaymentsTab from './tabs/PaymentsTab';
import ContactHistoryTab from './tabs/ContactHistoryTab';
import DocumentsTab from './tabs/DocumentsTab';
import NotesAuditTab from './tabs/NotesAuditTab';
import ApproveLoanModal from './actions/ApproveLoanModal';
import CancelLoanModal from './actions/CancelLoanModal';
import MarkDisbursedModal from './actions/MarkDisbursedModal';
import RecordPaymentModal from './actions/RecordPaymentModal';
import RequestRestructureModal from './actions/RequestRestructureModal';
import RecommendDefaultModal from './actions/RecommendDefaultModal';
import ForceFailDisbursementModal from './actions/ForceFailDisbursementModal';
import styles from './LoanDetailScreen.module.css';

const TABS = [
  'Overview',
  'Schedule',
  'Payments',
  'Contact history',
  'Documents',
  'Notes & audit',
] as const;
type Tab = (typeof TABS)[number];

const ACTION_LABEL: Record<LoanAction, string> = {
  approve: 'Approve & disburse',
  cancel: 'Reject',
  mark_disbursed: 'Mark disbursed',
  record_payment: 'Record payment',
  request_restructure: 'Request restructure',
  recommend_default: 'Recommend default',
  force_fail_disbursement: 'Force fail',
  reopen_cancelled: 'Request reopen',
};

const ACTION_ICON: Record<LoanAction, LucideIcon> = {
  approve: Check,
  cancel: X,
  mark_disbursed: Wallet,
  record_payment: CreditCard,
  request_restructure: RefreshCcw,
  recommend_default: AlertOctagon,
  force_fail_disbursement: Flag,
  reopen_cancelled: RotateCcw,
};

function actionVariant(a: LoanAction): 'primary' | 'secondary' | 'success' | 'danger' | 'outline' {
  if (a === 'approve') return 'success';
  if (a === 'cancel' || a === 'recommend_default') return 'danger';
  if (a === 'mark_disbursed') return 'primary';
  return 'secondary';
}

export default function LoanDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { can } = useRole();
  const { data: loan, isLoading, error, refetch } = useLoan(id);
  const [tab, setTab] = useState<Tab>('Overview');
  const [action, setAction] = useState<LoanAction | null>(null);

  const actions = useMemo(
    () => (loan ? allowedActions(loan.status) : []),
    [loan],
  );

  if (isLoading) return <div className={styles.state}>Loading loan…</div>;
  if (error) {
    return (
      <div className={styles.page}>
        <div className={styles.error}>{(error as Error).message}</div>
      </div>
    );
  }
  if (!loan) return <div className={styles.state}>Loan not found.</div>;

  const closeAction = (refresh: boolean) => {
    setAction(null);
    if (refresh) void refetch();
  };

  const borrowerName = loan.profiles?.display_name ?? loan.profiles?.full_name ?? '—';
  const submittedAt = formatCatDateTime(loan.applied_at);

  return (
    <div className={styles.page}>
      <button type="button" className={styles.back} onClick={() => navigate('/loans')}>
        <ChevronLeft size={14} /> Loan applications
      </button>

      <div className={styles.heroRow}>
        <div className={styles.heroLeft}>
          <div className={styles.heroIcon}><Wallet size={24} /></div>
          <div className={styles.heroText}>
            <div className={styles.heroChips}>
              <span className={styles.heroRef}>{loan.loan_number}</span>
              <StatusChip
                label={LOAN_STATUS_LABEL[loan.status]}
                tone={LOAN_STATUS_TONE[loan.status]}
              />
              <Chip tone="neutral" size="sm">
                {(loan.effective_annual_rate_bps / 100).toFixed(1)}% EAR
              </Chip>
            </div>
            <h1 className={styles.heroTitle}>
              {formatKwacha(loan.principal_ngwee as Ngwee, { decimals: 0 })} · {loan.term_days}-day loan
            </h1>
            <p className={styles.heroSub}>
              Submitted {submittedAt} by {borrowerName}
            </p>
          </div>
        </div>
        <div className={styles.heroActions}>
          <Button variant="ghost" size="md" leftIcon={<MoreHorizontal size={16} />} aria-label="More" />
          {actions.map((a) => {
            const enabled =
              (a === 'approve' && can('loans.approve')) ||
              (a === 'cancel' && can('loans.cancel')) ||
              (a === 'mark_disbursed' && can('loans.mark_disbursed')) ||
              (a === 'record_payment' && can('loans.record_payment')) ||
              (a === 'request_restructure' && can('loans.request_restructure')) ||
              (a === 'recommend_default' && can('loans.recommend_default')) ||
              (a === 'force_fail_disbursement' && can('loans.force_fail_disbursement')) ||
              (a === 'reopen_cancelled' && can('loans.request_restructure'));
            const Icon = ACTION_ICON[a];
            return (
              <Button
                key={a}
                variant={actionVariant(a)}
                size="md"
                onClick={() => setAction(a)}
                disabled={!enabled}
                leftIcon={<Icon size={14} />}
              >
                {ACTION_LABEL[a]}
              </Button>
            );
          })}
        </div>
      </div>

      <div className={styles.tabs} role="tablist">
        {TABS.map((t) => (
          <button
            key={t}
            type="button"
            role="tab"
            aria-selected={tab === t}
            onClick={() => setTab(t)}
            className={tab === t ? styles.tabActive : styles.tab}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === 'Overview' && <OverviewTab loan={loan} />}
      {tab === 'Schedule' && <ScheduleTab loan={loan} />}
      {tab === 'Payments' && <PaymentsTab loan={loan} />}
      {tab === 'Contact history' && <ContactHistoryTab loanId={loan.id} />}
      {tab === 'Documents' && <DocumentsTab loan={loan} />}
      {tab === 'Notes & audit' && <NotesAuditTab loan={loan} />}

      {action === 'approve' && <ApproveLoanModal loan={loan} onClose={closeAction} />}
      {action === 'cancel' && <CancelLoanModal loan={loan} onClose={closeAction} />}
      {action === 'mark_disbursed' && <MarkDisbursedModal loan={loan} onClose={closeAction} />}
      {action === 'record_payment' && <RecordPaymentModal loan={loan} onClose={closeAction} />}
      {action === 'request_restructure' && <RequestRestructureModal loan={loan} onClose={closeAction} />}
      {action === 'recommend_default' && <RecommendDefaultModal loan={loan} onClose={closeAction} />}
      {action === 'force_fail_disbursement' && <ForceFailDisbursementModal loan={loan} onClose={closeAction} />}
    </div>
  );
}
